import React, { useCallback, useContext, useMemo, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Button, Icon } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Backdrop,
  Button as MuiButton,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import PropTypes from "prop-types";
import { FileDownload } from "@mui/icons-material";
import {
  ReportSelectMUI,
  AccordionItem,
  AccordionDetailsItem,
  ReportsTable,
} from "../../../../components";
import {
  EMPLOYEE_PERMISSIONS_COLUMNS,
  generateEmployeePermissionsColumns,
} from "../../columns";

import ReportHeader from "../ReportHeader";

import { downloadLocalCsv, customToast as toast } from "../../../../utils";
import MobileTable from "../../../../components/ReportsTable/MobileTable";
import BoxLoader from "../../../../components/DetailedDataTable/BoxLoader/BoxLoader";
import { AuthContext } from "../../../../context/AuthContext";

const GET_EMPLOYEE_PERMISSIONS = gql`
  query GET_EMPLOYEE_PERMISSIONS(
    $orderBy: [UserOrderByWithRelationInput!]
    $where: UserWhereInput
  ) {
    users(orderBy: $orderBy, where: $where) {
      id
      employeeNumber
      firstName
      lastName
      costCode
      jobTitle
      group
      userType
      weldCertAccess
      showAssignedBy
    }
  }
`;

const UPDATE_USER_WELD_CERT_ACCESS = gql`
  mutation UpdateUserWeldCertAccess($userId: String!, $weldCertAccess: Boolean!) {
    updateUserWeldCertAccess(userId: $userId, weldCertAccess: $weldCertAccess) {
      id
      weldCertAccess
    }
  }
`;

const UPDATE_USER_SHOW_ASSIGNED_BY = gql`
  mutation UpdateUserShowAssignedBy($userId: String!, $showAssignedBy: Boolean!) {
    updateUserShowAssignedBy(userId: $userId, showAssignedBy: $showAssignedBy) {
      id
      showAssignedBy
    }
  }
`;

const EmployeePermissionsTable = ({ report, onReportChange, tabs, permissions }) => {
  const {
    state: { user: loggedInUser },
    setState: setAuthState,
  } = useContext(AuthContext);
  const [pendingWeldCertUpdates, setPendingWeldCertUpdates] = useState({});
  const [pendingShowAssignedByUpdates, setPendingShowAssignedByUpdates] = useState({});
  // get alpha list query repurposed for employee permissions
  const { data: { users: employees = [] } = {}, loading } = useQuery(
    GET_EMPLOYEE_PERMISSIONS,
    {
      variables: {
        where: {
          employmentStatus: {
            equals: "active",
          },
        },
        orderBy: [
          {
            lastName: "asc",
          },
        ],
      },
    }
  );
  const [updateUserWeldCertAccess] = useMutation(UPDATE_USER_WELD_CERT_ACCESS);
  const [updateUserShowAssignedBy] = useMutation(UPDATE_USER_SHOW_ASSIGNED_BY);

  const handleWeldCertAccessChange = useCallback(async (userId, weldCertAccess) => {
    try {
      setPendingWeldCertUpdates((prevState) => ({
        ...prevState,
        [userId]: true,
      }));

      await updateUserWeldCertAccess({
        variables: {
          userId,
          weldCertAccess,
        },
      });

      const toggledUser = employees.find((employee) => employee?.id === userId);
      const currentEmployeeNumber = `${loggedInUser?.employeeNumber || ""}`.trim();
      const toggledEmployeeNumber = `${toggledUser?.employeeNumber || ""}`.trim();
      const isLoggedInUser =
        loggedInUser?.id === userId ||
        (currentEmployeeNumber &&
          toggledEmployeeNumber &&
          currentEmployeeNumber === toggledEmployeeNumber);

      if (isLoggedInUser) {
        setAuthState((prevState) => {
          const updatedUser = {
            ...prevState.user,
            weldCertAccess,
          };

          try {
            window.localStorage.setItem("DECCO_USER", JSON.stringify(updatedUser));
          } catch (storageError) {
            // ignore storage errors
          }

          return {
            ...prevState,
            user: updatedUser,
          };
        });
      }
    } catch (err) {
      toast.error(
        "There was an error updating Weld Cert access. Please contact support."
      );
    } finally {
      setPendingWeldCertUpdates((prevState) => ({
        ...prevState,
        [userId]: false,
      }));
    }
  }, [employees, loggedInUser?.employeeNumber, loggedInUser?.id, setAuthState, updateUserWeldCertAccess]);

  const handleShowAssignedByChange = useCallback(async (userId, showAssignedBy) => {
    try {
      setPendingShowAssignedByUpdates((prevState) => ({
        ...prevState,
        [userId]: true,
      }));

      await updateUserShowAssignedBy({
        variables: {
          userId,
          showAssignedBy,
        },
      });

      const toggledUser = employees.find((employee) => employee?.id === userId);
      const currentEmployeeNumber = `${loggedInUser?.employeeNumber || ""}`.trim();
      const toggledEmployeeNumber = `${toggledUser?.employeeNumber || ""}`.trim();
      const isLoggedInUser =
        loggedInUser?.id === userId ||
        (currentEmployeeNumber &&
          toggledEmployeeNumber &&
          currentEmployeeNumber === toggledEmployeeNumber);

      if (isLoggedInUser) {
        setAuthState((prevState) => {
          const updatedUser = {
            ...prevState.user,
            showAssignedBy,
          };

          try {
            window.localStorage.setItem("DECCO_USER", JSON.stringify(updatedUser));
          } catch (storageError) {
            // ignore storage errors
          }

          return {
            ...prevState,
            user: updatedUser,
          };
        });
      }
    } catch (err) {
      toast.error(
        "There was an error updating Show Assigned By. Please contact support."
      );
    } finally {
      setPendingShowAssignedByUpdates((prevState) => ({
        ...prevState,
        [userId]: false,
      }));
    }
  }, [employees, loggedInUser?.employeeNumber, loggedInUser?.id, setAuthState, updateUserShowAssignedBy]);

  const columns = useMemo(
    () =>
      generateEmployeePermissionsColumns(
        handleWeldCertAccessChange,
        pendingWeldCertUpdates,
        handleShowAssignedByChange,
        pendingShowAssignedByUpdates
      ),
    [
      handleWeldCertAccessChange,
      pendingWeldCertUpdates,
      handleShowAssignedByChange,
      pendingShowAssignedByUpdates,
    ]
  );
  const isPermissionUpdateInProgress = useMemo(
    () =>
      Object.values(pendingWeldCertUpdates).some(Boolean) ||
      Object.values(pendingShowAssignedByUpdates).some(Boolean),
    [pendingWeldCertUpdates, pendingShowAssignedByUpdates]
  );

  const exportCSV = async () => {
    try {
      downloadLocalCsv(employees, "Employee Permissions Report", EMPLOYEE_PERMISSIONS_COLUMNS);
    } catch (err) {
      toast.error("Error exporting CSV");
    }
  };

  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));

  if (downSm) {
    return (
      <React.Fragment>
        <MobileTable
          showLoadingBar
          headerActions={
            <React.Fragment>
              <Box mt={1} width="100%">
                <ReportSelectMUI value={report} onChange={onReportChange} />
              </Box>

              <Box mt={1} width="20%">
                <MuiButton
                  color="secondary"
                  sx={{ color: "white", width: "100%" }}
                  variant="contained"
                  onClick={() => exportCSV()}
                >
                  <FileDownload />
                </MuiButton>
              </Box>
            </React.Fragment>
          }
          loading={loading}
          report={report}
          title="Reports"
          onExportClick={exportCSV}
          onReportChange={onReportChange}
        >
          {loading ? (
            <BoxLoader />
          ) : (
            employees?.map((item) => (
              <AccordionItem
                key={item?.employeeNumber}
                title={`${item?.lastName}, ${item?.firstName}`}
              >
                <AccordionDetailsItem
                  label="Employee #"
                  value={item?.employeeNumber}
                />
                <AccordionDetailsItem label="Permissions Level" value={item?.userType} />
                <AccordionDetailsItem
                  label="Weld Cert access"
                  value={item?.weldCertAccess ? "Enabled" : "Disabled"}
                />
                <AccordionDetailsItem
                  label="Show Assigned By"
                  value={item?.showAssignedBy ? "Enabled" : "Disabled"}
                />
              </AccordionItem>
            ))
          )}
        </MobileTable>
        <Backdrop
          open={isPermissionUpdateInProgress}
          sx={(muiTheme) => ({
            color: "#fff",
            zIndex: muiTheme.zIndex.modal + 1,
          })}
        >
          <CircularProgress color="inherit" size={28} />
        </Backdrop>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <ReportsTable
        columns={columns}
        data={employees}
        loading={loading}
        onExportClick={exportCSV}
      >
        {(permissions.webEditor ||
          permissions.superAdmin ||
          permissions.admin) && (
          <div className="table-utils">
            <Button
              className="download-btn"
              disabled={loading}
              size="medium"
              title="Download CSV"
              onClick={exportCSV}
            >
              <Icon size="small">
                <FontAwesomeIcon icon="download" />
              </Icon>
            </Button>
          </div>
        )}
        <ReportHeader
          report={report}
          tabs={tabs}
          onReportChange={onReportChange}
        />
      </ReportsTable>
      <Backdrop
        open={isPermissionUpdateInProgress}
        sx={(muiTheme) => ({
          color: "#fff",
          zIndex: muiTheme.zIndex.modal + 1,
        })}
      >
        <CircularProgress color="inherit" size={28} />
      </Backdrop>
    </React.Fragment>
  );
};

EmployeePermissionsTable.propTypes = {
  onReportChange: PropTypes.func.isRequired,
  report: PropTypes.string.isRequired,
  tabs: PropTypes.array.isRequired,
  permissions: PropTypes.object.isRequired,
};

export default EmployeePermissionsTable;
